import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useConfirmar } from "../Confirmar";
import Icon from "../Icon";
import { BtnPrimary } from "../Atoms";
import FlowCanvas from "./FlowCanvas";
import FlowToolbar from "./FlowToolbar";
import StepPanel, { ANCHO_PANEL } from "./StepPanel";
import AtajosModal from "./AtajosModal";
import { leer as leerAtajos, guardar as guardarAtajos } from "./atajos";
import { useFlowEditor } from "../../hooks/useFlowEditor";
import {
  NODE_GEO, rutaCritica, dentroDeLaVista, puertosDe, hitNode, portPoint, salidasDe,
  SALIDAS_POR_DEFECTO,
} from "./flowGeometry";
import { OBJETIVO_DIA } from "../../constants";
import useViewport from "../../hooks/useViewport";

// Une el lienzo, la barra y el panel. Aqui vive el pegamento con los datos; la
// interaccion esta en FlowCanvas y la matematica en flowGeometry.
export default function FlowView({
  proyecto, tareas, dependencias, currentUser, teams, minutosPorTarea, prevision, festivos = {},
  onCrearPaso, onCambiarPaso, onBorrarPaso,
  onCrearDependencia, onEditarDependencia, onBorrarDependencia,
  onGuardarPosiciones, showToast, crono, agendaTiposData = [], misPasos = [],
}) {
  const { isMobile } = useViewport();
  const [atajos, setAtajos] = useState(leerAtajos);
  const [verAtajos, setVerAtajos] = useState(false);
  const editable = !!proyecto?.can_edit && !isMobile;
  const rectRef = useRef({ left: 0, top: 0, width: 900, height: 600 });
  // Ultima posicion del puntero sobre el lienzo, en coordenadas de lienzo. Es
  // donde aparece un nodo creado con un atajo de teclado: nace donde estas
  // mirando, no en el centro de la vista.
  const cursorRef = useRef(null);
  const encajarAlTerminar = useRef(false);

  const ed = useFlowEditor({
    tareas, dependencias, editable,
    onGuardarPosiciones,
    onCrearPaso,
    onBorrarPaso,
    onCrearArista:  onCrearDependencia,
    onBorrarArista: onBorrarDependencia,
    onEditarArista: onEditarDependencia,
    showToast,
  });
  const { st, dispatch } = ed;

  // Ruta critica: se calcula sobre el grafo que hay en el lienzo, no en el
  // servidor, porque cambia con cada arista y no merece un viaje.
  const rc = useMemo(
    () => rutaCritica(st.nodes, st.edges, OBJETIVO_DIA),
    [st.nodes, st.edges]
  );

  // Coloca un nodo nuevo en el centro de lo que se esta viendo, y no en 0,0:
  // en 0,0 puede caer fuera de la pantalla y parece que no se ha creado nada.
  const centroVista = () => {
    const r = rectRef.current;
    const g = NODE_GEO.tarea;
    return {
      pos_x: Math.round(st.view.x + r.width  / (2 * st.view.k) - g.w / 2),
      pos_y: Math.round(st.view.y + r.height / (2 * st.view.k) - g.h / 2),
    };
  };

  const añadir = useCallback(async (kind, dondeElCursor = false) => {
    const meta = { tarea: "Paso nuevo", inicio: "Inicio", fin: "Fin",
                   condicional: "¿Condicion?", hito: "Hito", switch: "Reparte segun…" };
    // Con atajo, el nodo nace bajo el puntero; con el boton de la barra, en el
    // centro de la vista (pulsar un boton no dice nada de donde quieres el nodo,
    // y ademas el puntero esta sobre la propia barra).
    const g = NODE_GEO[kind] || NODE_GEO.tarea;
    // El cursor solo vale si esta DENTRO de lo que se ve. Si el puntero se fue
    // al panel, a la barra o fuera de la ventana, su ultima posicion conocida
    // puede estar en cualquier parte, y el nodo apareceria donde nadie mira.
    const bajoElCursor = dondeElCursor
      && dentroDeLaVista(cursorRef.current, rectRef.current, st.view, 20);
    const donde = bajoElCursor
      ? { pos_x: Math.round(cursorRef.current.x - g.w / 2),
          pos_y: Math.round(cursorRef.current.y - g.h / 2) }
      : centroVista();
    await ed.crearPaso({
      title: meta[kind] || "Paso nuevo",
      kind,
      status: "Pendiente",
      // Un switch nace con dos salidas: con cero no habria nada que conectar y
      // el nodo no se entenderia; con dos ya se ve para que sirve.
      ...(kind === "switch" ? { salidas: SALIDAS_POR_DEFECTO } : {}),
      ...donde,
    });
  }, [ed, st.view]); // eslint-disable-line

  // Un nodo se lleva sus conexiones por delante, asi que este si pregunta. Una
  // arista no: se rehace tirando de un puerto, y preguntar por cada una haria
  // insufrible limpiar un flujo.
  // Confirmacion dentro de la aplicacion, no window.confirm. El dialogo del
  // navegador tapa el lienzo entero y no deja ver de que paso esta hablando,
  // que es lo unico que hace falta para decidir; y no se cancela pulsando
  // fuera, que es el gesto natural para "dejalo".
  const [pedir, dialogo, confirmando] = useConfirmar();

  // Traer a la vista los nodos de los que va a hablar el dialogo. Se llama
  // ANTES de pintarlo, asi que cuando aparece la pregunta ya se esta mirando
  // lo que se va a tocar.
  // Cuanto del lienzo esta tapado por el panel. En movil el panel ocupa el ancho
  // entero y el diagrama entra en modo visor, asi que ahi no hay nada que
  // apartar: apartarlo seria empujar el diagrama fuera de la pantalla.
  const tapadoIzq = (ed.nodoSel || ed.aristaSel) && !isMobile ? ANCHO_PANEL : 0;

  const centrarEn = useCallback((ids) => {
    dispatch({ type: "SEL_MANY", ids: ids.map(String) });
    dispatch({ type: "CENTRAR", ids: ids.map(String), rect: rectRef.current, tapadoIzq });
  }, [dispatch, tapadoIzq]);

  const borrarPasoConAviso = useCallback(async (n) => {
    if (!n) return;
    const cx = ed.conexiones[String(n.id)];
    const cuantas = (cx?.salen.length || 0) + (cx?.llegan.length || 0);
    const ok = await pedir({
      titulo: `¿Eliminar «${n.title}»?`,
      mensaje: cuantas
        ? `Se llevara ${cuantas} conexion${cuantas > 1 ? "es" : ""} por delante.`
        : "No tiene conexiones.",
      etiquetaOk: "Eliminar", peligro: true,
      // Se centra la vista en el paso ANTES de preguntar: con el dialogo
      // abierto se ve de cual se esta hablando, que es justo lo que el dialogo
      // del navegador no dejaba.
      antes: () => centrarEn([n.id]),
    });
    if (ok) ed.borrarPaso(n.id);
  }, [ed, pedir, centrarEn]);

  // En bloque: un aviso para todos, no doce seguidos. Se dice cuantos pasos y
  // cuantas conexiones se van, porque el numero de conexiones es lo que la
  // gente no tiene en la cabeza al seleccionar con un barrido.
  //
  // Se borran en SERIE y no con Promise.all: cada borrado se lleva sus aristas
  // por delante, y en paralelo dos pasos que comparten una conexion intentarian
  // borrar la misma fila. En serie, si uno falla, los anteriores ya estan
  // hechos y el aviso del hook lo cuenta.
  const borrarPasosConAviso = useCallback(async (nodos) => {
    if (!nodos?.length) return;
    if (nodos.length === 1) return borrarPasoConAviso(nodos[0]);
    const aristas = new Set();
    for (const n of nodos) {
      const cx = ed.conexiones[String(n.id)];
      for (const e of [...(cx?.salen || []), ...(cx?.llegan || [])]) aristas.add(e.id);
    }
    const ok = await pedir({
      titulo: `¿Eliminar ${nodos.length} pasos?`,
      mensaje: aristas.size
        ? `Se llevaran ${aristas.size} conexion${aristas.size > 1 ? "es" : ""} por delante.`
        : "Ninguno tiene conexiones.",
      // Los nombres, porque despues de un barrido nadie recuerda exactamente
      // que ha cogido, y es lo que de verdad decide.
      detalle: nodos.map(n => n.title || "(sin titulo)").join(" · "),
      etiquetaOk: `Eliminar ${nodos.length}`, peligro: true,
      antes: () => centrarEn(nodos.map(n => n.id)),
    });
    if (!ok) return;
    for (const n of nodos) await ed.borrarPaso(n.id);
  }, [ed, borrarPasoConAviso, pedir, centrarEn]);

  const crearArista = useCallback(async (datos) => {
    // Los puertos que representan un CASO —el si/no de un condicional y cada
    // salida de un switch— admiten UNA sola flecha: un caso, un camino. Si ya
    // la tienen, se sustituye avisando.
    //
    // El puerto normal de un paso admite varias, y eso es intencional: es lo
    // que hace la divergencia.
    const origen = st.nodes[String(datos.from_task_id)];
    const esCaso = datos.from_handle && datos.from_handle !== "out"
      && (origen?.kind === "condicional" || origen?.kind === "switch");
    if (esCaso) {
      const previa = Object.values(st.edges).find(e =>
        String(e.from_task_id) === String(datos.from_task_id)
        && e.from_handle === datos.from_handle);
      if (previa) {
        await ed.borrarArista(previa.id);
        const nombre = origen.kind === "switch"
          ? (salidasDe(origen).find(x => x.clave === datos.from_handle)?.nombre || datos.from_handle)
          : (datos.from_handle === "si" ? "Si" : "No");
        showToast?.(`La salida «${nombre}» ya estaba conectada: se ha sustituido`, "warn");
      }
    }
    return ed.crearArista(datos);
  }, [ed, st.nodes, st.edges, showToast]);

  // Intercalar un paso en una flecha: A → B pasa a ser A → nuevo → B.
  //
  // El orden importa. Primero se BORRA la flecha vieja y despues se crean las
  // dos: si se creara antes la de entrada, un tramo que sale del si de un
  // condicional —o de una salida de un switch, que solo admiten una flecha—
  // veria su puerto ocupado por la arista que estamos a punto de quitar, y
  // crearArista avisaria de que "ya estaba conectada" sustituyendo la que no es.
  //
  // La de salida sale siempre por el puerto normal del paso intercalado. Si es
  // un condicional o un switch —que no tienen "out"— se usa su primera salida,
  // que es la unica eleccion que se puede tomar sin preguntar.
  const intercalar = useCallback(async ({ arista, nodo }) => {
    const id = String(nodo.id);
    if (String(arista.from_task_id) === id || String(arista.to_task_id) === id) return;

    const salida = puertosDe(nodo).salidas[0];
    if (!salida) return;

    const ok = await ed.borrarArista(arista.id);
    if (!ok) return;

    await ed.crearArista({
      from_task_id: arista.from_task_id, to_task_id: id,
      from_handle: arista.from_handle || "out", to_handle: "in",
      opcional: !!arista.opcional,
    });
    await ed.crearArista({
      from_task_id: id, to_task_id: arista.to_task_id,
      from_handle: salida, to_handle: arista.to_handle || "in",
      opcional: !!arista.opcional,
    });
  }, [ed]);

  // Conectar sin arrastrar: el atajo (o el boton) ARMA la flecha, y despues se
  // pulsa el paso de destino con el raton.
  //
  // Antes elegia el destino por proximidad. Era rapido pero adivinaba, y en un
  // diagrama con varios candidatos a la derecha acertaba poco: conectar es una
  // decision, no una busqueda.
  //
  // El origen es el nodo BAJO EL CURSOR si hay uno, y si no el seleccionado. Ese
  // orden y no el contrario: si tienes el raton encima de un paso, ese es el que
  // estas señalando.
  const conectar = useCallback((opcional = false) => {
    const bajo = cursorRef.current
      ? hitNode(st.nodes, cursorRef.current, st.order) : null;
    const desde = bajo ? String(bajo.id) : st.sel.nodes[0];
    if (!desde) {
      showToast?.("Pon el cursor sobre un paso, o selecciona uno, y vuelve a intentarlo", "warn");
      return;
    }

    const origen = st.nodes[desde];
    if (!puertosDe(origen.kind).salidas.length) {
      showToast?.("Un paso de fin no tiene salida", "warn");
      return;
    }
    // Un condicional tiene dos salidas con significado propio: cual de las dos
    // se quiere no lo puede adivinar un atajo, asi que ahi se tira del puerto.
    if (origen.kind === "condicional") {
      showToast?.("En un condicional, tira del puerto «Si» o «No» para elegir la rama", "warn");
      return;
    }
    // Igual con un switch: cual de sus salidas se quiere no lo puede adivinar
    // un atajo, y elegir la equivocada es peor que no hacer nada.
    if (origen.kind === "switch") {
      showToast?.("En un switch, tira del puerto de la salida que quieras conectar", "warn");
      return;
    }

    dispatch({ type: "DRAG_START", drag: {
      mode: "link", porTeclado: true, opcional,
      from: { nodeId: desde, port: "out" },
      to: portPoint(origen, "out"), candidato: null,
    }});
  }, [st.nodes, st.order, st.sel.nodes, dispatch, showToast]);

  // Reorganizar. Pide confirmacion porque pisa posiciones puestas a mano, y
  // despues encaja la vista: sin eso los nodos se recolocaban y la camara se
  // quedaba mirando a donde estaban antes, que es parte de lo que hacia que el
  // resultado se sintiera raro.
  const organizar = useCallback(async () => {
    const ok = await pedir({
      titulo: "¿Recolocar todos los pasos?",
      mensaje: "Se pierden las posiciones puestas a mano. Se puede deshacer con Ctrl+Z.",
      etiquetaOk: "Recolocar",
    });
    if (!ok) return;
    encajarAlTerminar.current = true;
    dispatch({ type: "LAYOUT" });
  }, [dispatch, pedir]);

  // Encajar cuando la animacion TERMINA de verdad, en vez de a los 360 ms a
  // ojo. Asi todo lo que se haya creado fuera de la pantalla entra en la vista,
  // que es el motivo de reorganizar cuando el diagrama se ha desperdigado.
  useEffect(() => {
    if (st.anim || !encajarAlTerminar.current) return;
    encajarAlTerminar.current = false;
    dispatch({ type: "FIT", rect: rectRef.current, tapadoIzq });
  }, [st.anim, dispatch, tapadoIzq]);

  // Al abrir el flujo se ordena y se encaja, como si se hubiera pulsado
  // reorganizar. Antes se abria en un rincon cualquiera del lienzo y con nodos
  // tapados por la barra, y lo primero que habia que hacer era colocarlo a mano.
  //
  // Los dos despachos van en el mismo efecto y en este orden a proposito: el
  // encaje necesita las posiciones YA aplicadas para calcular la caja, y el
  // reducer los procesa en secuencia.
  const abierto = useRef(false);
  useEffect(() => {
    if (abierto.current) return;
    if (!Object.keys(st.nodes).length) return;   // aun no ha llegado el grafo
    if (!rectRef.current.width) return;          // aun no se ha medido el lienzo
    abierto.current = true;
    dispatch({ type: "LAYOUT_INICIAL", persistir: editable });
    dispatch({ type: "FIT", rect: rectRef.current, tapadoIzq });
  }, [st.nodes, editable, dispatch]); // eslint-disable-line

  const nodos = Object.keys(st.nodes).length;

  return (
    <div className={"df-wrap" + (ed.nodoSel || ed.aristaSel ? " df-panel-izq" : "")}>
      <FlowCanvas st={st} dispatch={dispatch} editable={editable} pausado={confirmando}
        tapadoIzq={tapadoIzq}
        minutosPorTarea={minutosPorTarea} atajos={atajos}
        onCrearArista={crearArista}
        onBorrarArista={e => ed.borrarArista(e.id)}
        onIntercalar={intercalar}
        onBorrarPaso={borrarPasoConAviso}
        onBorrarPasos={borrarPasosConAviso}
        onAñadir={añadir}
        onOrganizar={organizar}
        onConectar={conectar}
        onDeshacer={ed.deshacer}
        onRehacer={ed.rehacer}
        onRect={r => { rectRef.current = r; }}
        onCursor={p => { cursorRef.current = p; }}
        onAbrirPaso={n => dispatch({ type: "SEL_NODE", id: n.id })} />

      <FlowToolbar editable={editable} guardando={ed.guardando} zoom={st.view.k}
        nodos={nodos}
        puedeDeshacer={ed.puedeDeshacer} puedeRehacer={ed.puedeRehacer}
        onAñadir={añadir}
        onDeshacer={ed.deshacer}
        onRehacer={ed.rehacer}
        onZoom={factor => dispatch({ type: "ZOOM_CENTRO", factor, rect: rectRef.current })}
        onEncajar={() => dispatch({ type: "FIT", rect: rectRef.current, tapadoIzq })}
        onConectar={conectar}
        conectando={st.drag?.mode === "link" && st.drag.porTeclado
          ? (st.drag.opcional ? "opcional" : "normal") : null}
        onAtajos={() => setVerAtajos(true)}
        atajos={atajos}
        onAutoOrganizar={organizar} />

      {/* Aviso de bucle: no se bloquea el ciclo, se explica */}
      {!!st.cycles.length && (
        <div style={{ position: "absolute", top: 10, right: 10, zIndex: 3, maxWidth: 260,
          background: "var(--warn-bg)", border: "1px solid var(--warn-line)", color: "var(--warn-text)",
          borderRadius: 10, padding: "7px 11px", fontSize: 11, lineHeight: 1.5 }}>
          <b>{st.cycles.length} conexion(es) cierran un bucle.</b> Está permitido —un retrabajo
          lo es— pero la ruta critica no se puede calcular mientras exista.
        </div>
      )}

      {/* Estado vacio: un lienzo en blanco no explica nada por si solo */}
      {!nodos && (
        <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center",
          justifyContent: "center", pointerEvents: "none" }}>
          <div style={{ textAlign: "center", maxWidth: 330, pointerEvents: "auto" }}>
            <Icon name="signpost-split" size={30} color="var(--text3)" />
            <div style={{ fontWeight: 700, fontSize: 14.5, marginTop: 11, color: "var(--text2)" }}>
              El flujo esta vacio
            </div>
            <div style={{ fontSize: 12, color: "var(--text3)", marginTop: 6, lineHeight: 1.6 }}>
              Añade pasos y tira de su puerto derecho hasta otro para conectarlos.
              Un condicional abre dos ramas, «Si» y «No».
            </div>
            {editable && (
              <div style={{ marginTop: 13, display: "flex", justifyContent: "center" }}>
                <BtnPrimary onClick={() => añadir("inicio")}>
                  <Icon name="play-fill" size={11} color="#fff" />Empezar con un Inicio
                </BtnPrimary>
              </div>
            )}
          </div>
        </div>
      )}

      {isMobile && proyecto?.can_edit && (
        <div style={{ position: "absolute", bottom: 10, left: 10, zIndex: 3,
          background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 20,
          padding: "3px 10px", fontSize: 10, color: "var(--text3)" }}>
          Modo lectura en movil: toca un nodo para verlo
        </div>
      )}

      {/* El dialogo va DENTRO del contenedor del flujo, no en la raiz de la
          aplicacion: asi su velo cubre el lienzo y no la cabecera ni el menu,
          y se lee como algo que pasa aqui. */}
      {dialogo}

      <StepPanel
        pedirConfirmacion={pedir}
        nodo={ed.nodoSel} arista={ed.aristaSel} nodos={st.nodes}
        proyecto={proyecto} currentUser={currentUser} teams={teams}
        conexiones={ed.conexiones} isMobile={isMobile}
        prevision={prevision} festivos={festivos}
        crono={crono} agendaTiposData={agendaTiposData} misPasos={misPasos}
        minutos={ed.nodoSel ? minutosPorTarea[String(ed.nodoSel.id)] : 0}
        onCambiarPaso={(id, patch) => {
          dispatch({ type: "PATCH_NODE", id, patch });   // reflejo inmediato
          onCambiarPaso?.(id, patch);
        }}
        onBorrarPaso={borrarPasoConAviso}
        onCambiarArista={(id, patch) => ed.editarArista(id, patch)}
        onBorrarArista={id => ed.borrarArista(id)}
        onSeleccionar={e => dispatch({ type: "SEL_EDGE", id: e.id })}
        onCerrar={() => dispatch({ type: "CLEAR_SEL" })} />

      {verAtajos && (
        <div className="modal-bg" onClick={() => setVerAtajos(false)}
          style={{ position: "absolute", inset: 0, zIndex: 20, display: "flex",
            alignItems: "center", justifyContent: "center", padding: 16,
            background: "rgba(0,0,0,.38)", backdropFilter: "blur(3px)" }}>
          <div className="modal-box" onClick={e => e.stopPropagation()}
            style={{ background: "var(--surface)", borderRadius: 16, padding: 22,
              width: "min(480px, 100%)", maxHeight: "100%", overflowY: "auto",
              boxShadow: "var(--shadow-lg)" }}>
            <AtajosModal mapa={atajos}
              onGuardar={m => { setAtajos(m); guardarAtajos(m); }}
              onCerrar={() => setVerAtajos(false)} />
          </div>
        </div>
      )}

      {/* Ruta critica, abajo a la izquierda cuando hay algo que decir */}
      {!st.cycles.length && rc.ids.length > 1 && (
        <div className="df-ruta"
          title="El camino mas largo del flujo: es lo que marca la duracion del proyecto">
          <b style={{ color: "var(--text2)" }}>Ruta critica</b> · {rc.ids.length} pasos ·
          {" "}{rc.dias} dias
        </div>
      )}
    </div>
  );
}

